export const OPTIMIZE_SCOPE_STORAGE_KEY = 'nm.optimizeScope';

export function normalizeSerial(serial) {
  return String(serial || '').trim().toLowerCase();
}

export function listCapDevices(devices = []) {
  return (devices || []).filter((d) => {
    const model = (d.capabilities?.model || '').toLowerCase();
    return model.includes('cap');
  });
}

export function expandCapScopeSerials(devices, capSerials) {
  const capSet = new Set((capSerials || []).map(normalizeSerial).filter(Boolean));
  if (capSet.size === 0) return [];

  const out = new Set();
  (devices || []).forEach((d) => {
    const serial = normalizeSerial(d.serial_number);
    if (!serial) return;
    const cap = normalizeSerial(d.cap_serial);
    if (capSet.has(serial) || capSet.has(cap)) {
      out.add(d.serial_number);
    }
  });
  (capSerials || []).forEach((cap) => {
    const trimmed = String(cap || '').trim();
    if (trimmed) out.add(trimmed);
  });
  return [...out];
}

export function persistOptimizeScope(scope) {
  if (!scope) return;
  sessionStorage.setItem(OPTIMIZE_SCOPE_STORAGE_KEY, JSON.stringify(scope));
}

export function consumeOptimizeScope() {
  try {
    const raw = sessionStorage.getItem(OPTIMIZE_SCOPE_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(OPTIMIZE_SCOPE_STORAGE_KEY);
    return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(OPTIMIZE_SCOPE_STORAGE_KEY);
    return null;
  }
}

export function buildOptimizeScopePayload(scopeMode, { selectedCapSerials = [], mapFocusSerials = [] } = {}) {
  if (scopeMode === 'caps' && selectedCapSerials.length > 0) {
    return { cap_serials: selectedCapSerials };
  }
  if (scopeMode === 'devices' && mapFocusSerials.length > 0) {
    return { focus_serials: mapFocusSerials };
  }
  return {};
}

export function scopeSummaryLabel(
  scopeMode,
  { selectedCapSerials = [], mapFocusSerials = [], selectedCustomTrees = [] } = {},
  caps = [],
) {
  if (scopeMode === 'devices' && mapFocusSerials.length > 0) {
    return `${mapFocusSerials.length} device(s) from map selection`;
  }
  if (scopeMode === 'trees' && selectedCustomTrees.length > 0) {
    if (selectedCustomTrees.length === 1) {
      return selectedCustomTrees[0].name || selectedCustomTrees[0].id;
    }
    return `${selectedCustomTrees.length} custom trees`;
  }
  if (scopeMode === 'caps' && selectedCapSerials.length > 0) {
    if (caps.length > 1 && selectedCapSerials.length === caps.length) {
      return 'All Site';
    }
    if (selectedCapSerials.length === 1) return selectedCapSerials[0];
    return `${selectedCapSerials.length} CAP(s)`;
  }
  if (scopeMode === 'caps') {
    return 'No CAPs selected';
  }
  return 'All Site';
}

export function resolveOptimizeScopeSerials(scopeMode, {
  selectedCapSerials = [],
  mapFocusSerials = [],
  selectedCustomTrees = [],
  devices = [],
  optimizeScopeIsSiteWide = true,
} = {}) {
  if (optimizeScopeIsSiteWide) return null;
  if (scopeMode === 'devices' && mapFocusSerials.length > 0) {
    return mapFocusSerials.map(normalizeSerial).filter(Boolean);
  }
  if (scopeMode === 'trees' && selectedCustomTrees.length > 0) {
    const out = new Set();
    selectedCustomTrees.forEach((tree) => {
      (tree.devices || []).forEach((serial) => {
        const normalized = normalizeSerial(serial);
        if (normalized) out.add(normalized);
      });
      (tree.caps || []).forEach((serial) => {
        const normalized = normalizeSerial(serial);
        if (normalized) out.add(normalized);
      });
    });
    return out.size > 0 ? [...out] : null;
  }
  if (scopeMode === 'caps' && selectedCapSerials.length > 0) {
    const expanded = expandCapScopeSerials(devices, selectedCapSerials)
      .map(normalizeSerial)
      .filter(Boolean);
    return expanded.length > 0 ? expanded : null;
  }
  return null;
}

export function filterFreshnessToScope(freshness, devices, scopeSerials) {
  if (!freshness || !scopeSerials?.length) return freshness;
  const scope = new Set(scopeSerials.map(normalizeSerial).filter(Boolean));
  if (scope.size === 0) return freshness;

  const out = {
    stale_threshold_s: freshness.stale_threshold_s,
    oldest_age_s: 0,
    oldest_serial: '',
    stale_count: 0,
    stale_serials: [],
    is_stale: false,
    devices_with_data: 0,
    devices_missing_data: 0,
    missing_serials: [],
    connected_jnet2_devices: 0,
    cap_devices: 0,
  };

  (devices || []).forEach((entry) => {
    const serial = normalizeSerial(entry.serial);
    if (!scope.has(serial)) return;
    if (!entry.connected) return;
    if (entry.is_cap) {
      out.cap_devices += 1;
      return;
    }
    out.connected_jnet2_devices += 1;
    if (!entry.has_data) {
      out.devices_missing_data += 1;
      out.missing_serials.push(entry.serial);
      return;
    }
    out.devices_with_data += 1;
    const age = entry.age_seconds ?? 0;
    if (age > out.oldest_age_s) {
      out.oldest_age_s = age;
      out.oldest_serial = entry.serial;
    }
    if (entry.stale) {
      out.stale_count += 1;
      out.stale_serials.push(entry.serial);
    }
  });

  out.is_stale = out.stale_count > 0;
  out.missing_serials.sort();
  out.stale_serials.sort();
  return out;
}

export function filterDevicesToScopeSerials(devices, scopeSerials) {
  if (!scopeSerials?.length) return devices || [];
  const scope = new Set(scopeSerials.map(normalizeSerial).filter(Boolean));
  return (devices || []).filter((d) => scope.has(normalizeSerial(d.serial_number)));
}

export function filterMissingSerialsToScope(missingSerials, scopeSerials) {
  if (!scopeSerials?.length || !missingSerials?.length) return missingSerials || [];
  const scope = new Set(scopeSerials.map(normalizeSerial).filter(Boolean));
  return missingSerials.filter((serial) => scope.has(normalizeSerial(serial)));
}

export function optimizeScopeHelpText(scopeMode, {
  selectedCapSerials = [],
  mapFocusSerials = [],
  selectedCustomTrees = [],
  caps = [],
} = {}) {
  if (scopeMode === 'trees') {
    if (selectedCustomTrees.length === 0) {
      return 'Select one or more custom trees. Only cohort members are loaded into the planner; neighbour scans are required for those Nodes only.';
    }
    if (selectedCustomTrees.length === 1) {
      return 'Isolated cohort: only this custom tree is loaded into the planner. Neighbour scans are required for its Nodes only; the rest of the site is ignored.';
    }
    return `Isolated cohort: ${selectedCustomTrees.length} custom trees are unioned into one planner world. Neighbour scans are required for those Nodes only; the rest of the site is ignored.`;
  }
  if (scopeMode === 'devices' && mapFocusSerials.length > 0) {
    return 'Isolated cohort: only the map-selected devices are loaded into the planner. Neighbour scans are required for those Nodes only; the rest of the site is ignored.';
  }
  if (scopeMode === 'caps') {
    if (caps.length > 0 && selectedCapSerials.length === caps.length) {
      return 'Whole-site optimize: all devices and neighbour data feed the planner.';
    }
    if (selectedCapSerials.length <= 1) {
      return 'Isolated cohort: only the selected CAP tree is loaded into the planner (the CAP and its Nodes). Neighbour scans are required for those Nodes only; the rest of the site is ignored.';
    }
    return `Isolated cohort: ${selectedCapSerials.length} selected CAP trees are loaded into the planner (each CAP and its Nodes). Neighbour scans are required for those Nodes only; the rest of the site is ignored.`;
  }
  return 'Whole-site optimize: all devices and neighbour data feed the planner.';
}
